import {
  isObject,
  type DocumentReference,
  type INamedDocument,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { IAccountCreditRefunded } from '../account-credit/account-credit';
import { type IAppointment } from '../appointment/appointment';
import { ServiceCodeType } from '../clinical-charting/service-provider/service-item';
import { type ITreatmentPlan } from '../clinical-charting/treatment/treatment-plan';
import { type IPatient } from '../patient/patient';
import { type IStaffer } from '../staffer/staffer';
import { ITreatmentCategory } from '../treatment-category';
import {
  isInvoiceLineItem,
  type IHasLineItems,
  type IInvoiceLineItem,
} from './line-item';

export enum InvoiceLineItemType {
  Product = 'product',
  Treatment = 'treatment',
  TreatmentBasePrice = 'treatmentBasePrice',
  ServiceCode = 'adaCode',
  Deposit = 'deposit',
  CreditTransfer = 'creditTransfer',
  CreditRefund = 'creditRefund',
  Refund = 'refund',
  Fee = 'fee',
}

export enum RefundType {
  Card = 'card',
  Credit = 'credit',
}

export interface ICustomLineItem extends IInvoiceLineItem {
  type: InvoiceLineItemType;
}

export function isCustomLineItem(item: unknown): item is ICustomLineItem {
  return isInvoiceLineItem(item) && isObject(item) && isString(item.type);
}

export interface IAttributedTo {
  attributedTo?: INamedDocument<IStaffer>;
}

export type IProductLineItem = ICustomLineItem & IAttributedTo;
export type IFeeLineItem = ICustomLineItem & IAttributedTo;

export interface IDepositLineItem extends ICustomLineItem, IAttributedTo {
  max: number;
  treatments: INamedDocument[];
  forTreatmentCategoryRef?: DocumentReference<ITreatmentCategory>;
  appointmentRef?: DocumentReference<IAppointment>;
}

export type IAttributableLineItem =
  | IDepositLineItem
  | IProductLineItem
  | IFeeLineItem;

export interface IServiceCodeLineItem<T = object> extends ICustomLineItem {
  code: string;
  toothId?: string;
  serviceCodeType?: ServiceCodeType;
  extendedData?: T;
}

export interface ITreatmentRef {
  planRef?: DocumentReference<ITreatmentPlan>;
  treatmentUuid: string;
  attributedTo: INamedDocument<IStaffer>;
}

export interface ITreatmentBasePriceLineItem extends ICustomLineItem {
  treatmentRef: ITreatmentRef;
}

export interface ITreatmentLineItem
  extends ICustomLineItem,
    IHasLineItems<IServiceCodeLineItem | ITreatmentBasePriceLineItem> {
  treatmentRef: ITreatmentRef;
  patientRef?: DocumentReference<IPatient>;
}

export interface ICreditTransferLineItem
  extends ICustomLineItem,
    IAttributedTo {
  patientTransferredRefs: {
    from: DocumentReference<IPatient>;
    to: DocumentReference<IPatient>;
  };
}

export interface ICreditRefundLineItem extends ICustomLineItem {
  creditsUsed: IAccountCreditRefunded[];
}

export interface IRefundLineItem extends ICustomLineItem {
  refundType: RefundType;
  sourceInvoice: DocumentReference;
}

export function lineItemIsType<T extends ICustomLineItem>(
  type: InvoiceLineItemType
): (item: ICustomLineItem) => item is T {
  return (item: ICustomLineItem): item is T => item.type === type;
}

type LineItemFilter<T extends ICustomLineItem> = (
  item: ICustomLineItem
) => item is T;

export const isProductLineItem: LineItemFilter<IProductLineItem> =
  lineItemIsType<IProductLineItem>(InvoiceLineItemType.Product);

export const isTreatmentLineItem: LineItemFilter<ITreatmentLineItem> =
  lineItemIsType<ITreatmentLineItem>(InvoiceLineItemType.Treatment);

export const isTreatmentBasePriceLineItem: LineItemFilter<ITreatmentLineItem> =
  lineItemIsType<ITreatmentLineItem>(InvoiceLineItemType.TreatmentBasePrice);

export const isDepositLineItem: LineItemFilter<IDepositLineItem> =
  lineItemIsType<IDepositLineItem>(InvoiceLineItemType.Deposit);

export const isFeeLineItem: LineItemFilter<IFeeLineItem> =
  lineItemIsType<IFeeLineItem>(InvoiceLineItemType.Fee);

export const isRefundLineItem: LineItemFilter<IRefundLineItem> =
  lineItemIsType<IRefundLineItem>(InvoiceLineItemType.Refund);

export const isCreditTransferLineItem: LineItemFilter<ICreditTransferLineItem> =
  lineItemIsType<ICreditTransferLineItem>(InvoiceLineItemType.CreditTransfer);

export const isCreditRefundLineItem: LineItemFilter<ICreditRefundLineItem> =
  lineItemIsType<ICreditRefundLineItem>(InvoiceLineItemType.CreditRefund);

export function isAttributableLineItem(
  item: ICustomLineItem
): item is IAttributableLineItem {
  return (
    isDepositLineItem(item) || isProductLineItem(item) || isFeeLineItem(item)
  );
}
